<template>
  <div class="card border-0 shadow-none rounded-3 mb-4 settings-card">
    <div class="card-body p-4 p-xl-40">
      <div class="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
        <div>
          <span class="settings-chip">{{ $t("system_settings_page.receipt_rules") }}</span>
          <h4 class="mb-2 text-title fw-semibold mt-3">
            {{ $t("system_settings_page.pos_receipt_settings_title") }}
          </h4>
          <p class="text-optional mb-0 settings-subtitle">
            {{ $t("system_settings_page.pos_receipt_settings_description") }}
          </p>
        </div>
      </div>

      <div class="row g-4">
        <div class="col-lg-8">
          <div class="row g-3">
            <div v-for="option in receiptOptions" :key="option.key" class="col-md-6">
              <label class="receipt-option">
                <div>
                  <div class="receipt-option-title">{{ option.label }}</div>
                  <div class="receipt-option-text">{{ option.description }}</div>
                </div>
                <input
                  :checked="Boolean(settings[option.key])"
                  class="form-check-input receipt-check"
                  type="checkbox"
                  @change="patchSettings({ [option.key]: ($event.target as HTMLInputElement).checked } as Partial<Setting>)"
                />
              </label>
            </div>
          </div>
        </div>

        <div class="col-lg-4">
          <div class="receipt-preview-box">
            <div class="receipt-preview-title">{{ $t("system_settings_page.receipt_preview_title") }}</div>
            <ul class="receipt-preview-list">
              <li>{{ $t("system_settings_page.receipt_preview_item_pos") }}</li>
              <li>{{ $t("system_settings_page.receipt_preview_item_sales") }}</li>
              <li>{{ $t("system_settings_page.receipt_preview_item_branding") }}</li>
            </ul>
          </div>
        </div>

        <div class="col-12">
          <label class="settings-label">{{ $t("system_settings_page.note_to_customer") }}</label>
          <textarea
            :value="settings.receipt_note_to_customer"
            rows="3"
            class="form-control settings-input settings-textarea"
            :placeholder="$t('system_settings_page.note_to_customer_placeholder')"
            :disabled="!settings.receipt_show_note_to_customer"
            @input="patchSettings({ receipt_note_to_customer: ($event.target as HTMLTextAreaElement).value })"
          ></textarea>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";
import { useI18n } from "vue-i18n";

import type { Setting } from "@/services/setting";

type ReceiptKey =
  | "receipt_show_phone"
  | "receipt_show_address"
  | "receipt_show_email"
  | "receipt_show_customer"
  | "receipt_show_tax_discount"
  | "receipt_show_barcode"
  | "receipt_show_note_to_customer";

export default defineComponent({
  name: "PosReceiptSettings",
  emits: ["update:settings"],
  props: {
    settings: {
      type: Object as PropType<Setting>,
      required: true,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();

    const receiptOptions = computed<
      Array<{
        key: ReceiptKey;
        label: string;
        description: string;
      }>
    >(() => [
      {
        key: "receipt_show_phone",
        label: t("system_settings_page.receipt_show_phone"),
        description: t("system_settings_page.receipt_show_phone_description"),
      },
      {
        key: "receipt_show_address",
        label: t("system_settings_page.receipt_show_address"),
        description: t("system_settings_page.receipt_show_address_description"),
      },
      {
        key: "receipt_show_email",
        label: t("system_settings_page.receipt_show_email"),
        description: t("system_settings_page.receipt_show_email_description"),
      },
      {
        key: "receipt_show_customer",
        label: t("system_settings_page.receipt_show_customer"),
        description: t("system_settings_page.receipt_show_customer_description"),
      },
      {
        key: "receipt_show_tax_discount",
        label: t("system_settings_page.receipt_show_tax_discount"),
        description: t("system_settings_page.receipt_show_tax_discount_description"),
      },
      {
        key: "receipt_show_barcode",
        label: t("system_settings_page.receipt_show_barcode"),
        description: t("system_settings_page.receipt_show_barcode_description"),
      },
      {
        key: "receipt_show_note_to_customer",
        label: t("system_settings_page.receipt_show_note_to_customer"),
        description: t("system_settings_page.receipt_show_note_to_customer_description"),
      },
    ]);

    const patchSettings = (partial: Partial<Setting>) => {
      emit("update:settings", {
        ...props.settings,
        ...partial,
      });
    };

    return {
      receiptOptions,
      patchSettings,
    };
  },
});
</script>

<style scoped>
.settings-card {
  background: linear-gradient(180deg, #ffffff 0%, #fbfcff 100%);
}

.settings-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: rgba(96, 93, 255, 0.12);
  color: #605dff;
  font-size: 13px;
  font-weight: 700;
  padding: 10px 14px;
}

.settings-subtitle {
  max-width: 720px;
}

.settings-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 10px;
}

.settings-input {
  min-height: 54px;
  border-radius: 16px;
  border: 1px solid #d7def4;
  background: #ffffff;
  color: #0f172a;
  box-shadow: none;
  padding: 14px 18px;
}

.settings-input:focus {
  border-color: #605dff;
  box-shadow: 0 0 0 4px rgba(96, 93, 255, 0.12);
}

.settings-textarea {
  min-height: 110px;
  padding-top: 16px;
}

.receipt-option {
  height: 100%;
  border: 1px solid #e2e8f5;
  border-radius: 18px;
  background: #f9fbff;
  padding: 18px 18px;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  cursor: pointer;
}

.receipt-option-title {
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 4px;
}

.receipt-option-text {
  font-size: 13px;
  color: #64748b;
}

.receipt-check {
  width: 20px;
  height: 20px;
  margin-top: 4px;
  box-shadow: none;
}

.receipt-preview-box {
  border-radius: 20px;
  background: #eef2ff;
  border: 1px solid #d7def4;
  padding: 20px;
  height: 100%;
}

.receipt-preview-title {
  font-size: 15px;
  font-weight: 700;
  color: #312e81;
  margin-bottom: 12px;
}

.receipt-preview-list {
  margin: 0;
  padding-left: 18px;
  color: #475569;
}

.receipt-preview-list li + li {
  margin-top: 8px;
}
</style>
